import {
  type ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { SourceEntity } from '../../../source/source-entity';
import { type IBaseMigrationItemCode } from '../../../interfaces';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { TypeGuard } from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import {
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';

export const ADA_ITEM_RESOURCE_TYPE = 'practiceWorksADAItem';

export const ADA_ITEM_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Practice Works ADA Item List',
    description: 'This is the list of item codes used to charge for services',
    idPrefix: ADA_ITEM_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IPracticeWorksItemCode extends IBaseMigrationItemCode {
  id: number;
  groupId: number;
  displayCode: string;
}

export function isPracticeWorksItemCode(
  item: unknown,
  logError: boolean
): item is IPracticeWorksItemCode {
  return TypeGuard.interface<IPracticeWorksItemCode>(
    {
      id: isNumber,
      groupId: isNumber,
      itemCode: isString,
      displayCode: TypeGuard.nilOr(isString),
      description: isString,
    },
    logError
  )(item);
}

const ADA_ITEM_SOURCE_QUERY = `
SELECT
  treatment_code.tx_code_id AS id,
  treatment_code.tx_code_group_id AS group_id,
  convert_to_text(treatment_code.medical_code) AS item_code,
  NULLIF(treatment_code.user_id, '') AS display_code,
  treatment_code.description
FROM
  _tx_code_defs_ AS treatment_code
WHERE
  treatment_code.medical_code IS NOT NULL
  AND treatment_code.tx_code_id IS NOT NULL
  AND EXISTS (
    SELECT 1
    FROM _ledger_history_ AS ledger
    WHERE ledger.tx_code_id = treatment_code.tx_code_id
  )
`;

export type PracticeWorksADAItem = ISourceEntityTypes<IPracticeWorksItemCode>;

export class ADAItemSourceEntity extends BaseSourceEntity<PracticeWorksADAItem> {
  sourceEntity = ADA_ITEM_SOURCE_ENTITY;
  entityResourceType = ADA_ITEM_RESOURCE_TYPE;
  sourceQuery = ADA_ITEM_SOURCE_QUERY;
  verifySourceFn = isPracticeWorksItemCode;

  override transformDataFn = flow([
    convertKeysToCamelCaseFn(),
    convertNullToUndefinedFn(),
    convertValueFn((val) => {
      if (isString(val) && val.length === 2) {
        return `0${val}`;
      }
      return val;
    }, 'itemCode'),
  ]);

  translate(): object {
    return {};
  }

  override getSourceRecordId(data: IPracticeWorksItemCode): number {
    return data.id;
  }

  getSourceLabel(data: IPracticeWorksItemCode): string {
    return `${data.itemCode} - ${data.description}`;
  }
}
